// location-cascade-dropdown-component.ts - 完整最終版本

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  forwardRef,
  Injector,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropdownOptionsLocationService } from 'src/app/shared/service/dropdown-options-location-service.service';
import { LocationOption, LocationData } from '../../model/LocationOption';
import { SessionBaseComponent } from '../../session.base.component';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { AddressServiceService } from 'src/app/core/services/address-service.service';
import { NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
@Component({
  selector: 'laas-location-cascade-dropdown',
  templateUrl: './location-cascade-dropdown-component.html',
  styleUrls: ['./location-cascade-dropdown-component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationCascadeDropdownComponent),
      multi: true,
    },
        {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LocationCascadeDropdownComponent),
      multi: true,
    },
  ],
})
export class LocationCascadeDropdownComponent
  extends SessionBaseComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  // Display configuration inputs
  @Input() showCountry: boolean = true;
  @Input() showCity: boolean = true;
  @Input() showArea: boolean = true;
  @Input() showExpressArea: boolean = false;
  @Input() showDistrict: boolean = false;

  // MultiSelect configuration
  @Input() useMultiSelectForCountry: boolean = false;
  @Input() useMultiSelectForCity: boolean = false;
  @Input() useMultiSelectForArea: boolean = false;
  @Input() useMultiSelectForExpressArea: boolean = false;
  @Input() useMultiSelectForDistrict: boolean = false;

  // Label customization
  @Input() countryLabel: string = 'Country';
  @Input() cityLabel: string = 'City';
  @Input() areaLabel: string = 'Area';
  @Input() expressAreaLabel: string = 'Express Area';
  @Input() districtLabel: string = 'District';

  // Placeholder customization
  @Input() countryPlaceholder: string = 'Choose Country';
  @Input() cityPlaceholder: string = 'Choose City';
  @Input() areaPlaceholder: string = 'Choose Area';
  @Input() expressAreaPlaceholder: string = 'Choose Express Area';
  @Input() districtPlaceholder: string = 'Choose District';

  // Styling and features
  @Input() cssClasses: string = 'col-12 md:col-6 lg:col-3 mb-3';
  @Input() labelClasses: string = '';
  @Input() valueClasses: string = 'item';

  @Input() showClear: boolean = true;
  @Input() enableFilter: boolean = true;
  @Input() allowAllOption: boolean = true;
  @Input() virtualScroll: boolean = false;
  @Input() itemSize: number = 40;

  // Validation
  @Input() submitted: boolean = false;
  @Input() showValidation: boolean = true;
  @Input() requiredFields: string[] = [];

  // Selection Events
  @Output() countryChange = new EventEmitter<{
    value: any;
    option?: LocationOption;
  }>();
  @Output() cityChange = new EventEmitter<{
    value: any;
    option?: LocationOption;
  }>();
  @Output() areaChange = new EventEmitter<{
    value: any;
    option?: LocationOption;
  }>();
  @Output() expressAreaChange = new EventEmitter<{
    value: any;
    option?: LocationOption;
  }>();
  @Output() districtChange = new EventEmitter<{
    value: any;
    option?: LocationOption;
  }>();
  @Output() selectionComplete = new EventEmitter<LocationData>();

  // Options Ready Events - 供父元件做查詢結果 mapping 使用
  @Output() countryOptionsReady = new EventEmitter<LocationOption[]>();
  @Output() cityOptionsReady = new EventEmitter<LocationOption[]>();
  @Output() areaOptionsReady = new EventEmitter<LocationOption[]>();
  @Output() expressAreaOptionsReady = new EventEmitter<LocationOption[]>();
  @Output() districtOptionsReady = new EventEmitter<LocationOption[]>();

  // Internal properties
  form: FormGroup;

  // Dropdown options
  countryOptions: LocationOption[] = [];
  cityOptions: LocationOption[] = [];
  areaOptions: LocationOption[] = [];
  expressAreaOptions: LocationOption[] = [];
  districtOptions: LocationOption[] = [];

  // All data for filtering
  private allCountries: LocationOption[] = [];
  private allCities: LocationOption[] = [];
  private allAreas: LocationOption[] = [];
  private allExpressAreas: LocationOption[] = [];

  //loading
  private pendingValue: LocationData | null = null;
  private optionsInitialized: boolean = false;

  private destroy$ = new Subject<void>();
  private onChange = (value: LocationData) => {};
  private onTouched = () => {};

  constructor(
    inject: Injector,
    private fb: FormBuilder,
    private locationService: DropdownOptionsLocationService,
    private commonDataService: CommonDataService,
    private addressServiceService : AddressServiceService
  ) {
    super(inject);
    this.form = this.fb.group({
      country: [null],
      city: [{ value: null, disabled: true }],
      area: [{ value: null, disabled: true }],
      expressArea: [{ value: null, disabled: true }],
      district: [{ value: null, disabled: true }],
    });
  }

  async ngOnInit(): Promise<void> {
    this.setupFormValueChanges();
    await this.loadLocationData();
    this.updateFieldValidators();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ControlValueAccessor writeValue - 智能延遲版本
   * 如果選項還沒載入，先暫存值，等載入完成後再套用
   */
  writeValue(value: LocationData | null): void {
    console.log('writeValue called with:', value);
    console.log('Options initialized:', this.optionsInitialized);
    console.log('Country options length:', this.countryOptions.length);

    // 如果選項還沒初始化，先暫存值
    if (!this.optionsInitialized || this.countryOptions.length === 0) {
      console.log('Options not ready, storing value for later');
      this.pendingValue = value;
      return;
    }

    // 選項已準備好，執行設定
    this.applyValue(value);
  }

  /**
   * 實際套用值到表單
   */
  private applyValue(value: LocationData | null): void {
    if (value) {
      console.log('Applying value to form:', value);

      this.form.patchValue(value, { emitEvent: false });

      // Update dependent dropdowns based on the written values
      this.updateCitiesForCountry(value.country, false);
      if (value.city) {
        this.updateAreasForCity(value.city, false);
        this.updateExpressAreasForCity(value.city, false);
        this.updateDistrictsForCity(value.country, value.city, false);
      }

      console.log(' Value successfully applied, form state:', this.form.value);
    } else {
      this.form.reset({ emitEvent: false });
      this.resetAllChildControls();
    }
    this.updateFieldValidators();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // ==================== 表單值變化監聽 ====================

  private setupFormValueChanges(): void {
    this.form.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      const locationData: LocationData = {
        country: value.country,
        city: value.city,
        area: value.area,
        expressArea: value.expressArea,
        district: value.district
      };

      this.onChange(locationData);
      this.onTouched();
      this.checkSelectionComplete(locationData);
    });
  }

  private onValidatorChange = () => {};

  registerOnValidatorChange(fn: () => void): void {
      this.onValidatorChange = fn;
  }

  /**
   * 載入位置資料 - 載入完成後檢查是否有待套用的值
   */
  private async loadLocationData(): Promise<void> {
    try {
      console.log('Loading location options...');

      const [countries, cities, areas, expressAreas] = await Promise.all([
        this.locationService.getCountries(),
        this.locationService.getCities(),
        this.locationService.getAreas(),
        this.locationService.getExpressAreas()
      ]);

      this.allCountries = countries;
      this.allCities = cities;
      this.allAreas = areas;
      this.allExpressAreas = expressAreas;

      this.countryOptions = this.prepareOptions(this.allCountries);

      // 發送 options ready 事件
      this.countryOptionsReady.emit(this.allCountries);
      this.cityOptionsReady.emit(this.allCities);
      this.areaOptionsReady.emit(this.allAreas);
      this.expressAreaOptionsReady.emit(this.allExpressAreas);

      // 標記選項已初始化
      this.optionsInitialized = true;
      console.log('Location data loaded successfully, options initialized');
      console.log('Country options:', this.countryOptions.length);
      console.log(' City options:', this.allCities.length);
      console.log('Area options:', this.allAreas.length);

      // 檢查是否有待套用的值
      if (this.pendingValue) {
        console.log(' Applying pending value:', this.pendingValue);
        this.applyValue(this.pendingValue);
        this.pendingValue = null;
      }

    } catch (error) {
      console.error('Failed to load location data:', error);
    }
  }

  /**
   * Handle country selection change
   */
  onCountryChange(selectedValue: any): void {
    const selectedOption = this.countryOptions.find(
      (opt) => opt.value === selectedValue
    );

    // Clear dependent fields
    this.form.patchValue(
      {
        city: null,
        area: null,
        expressArea: null,
        district: null,
      },
      { emitEvent: false }
    );

    // Update cities
    this.updateCitiesForCountry(selectedValue);

    // Clear and disable all dependent controls
    this.clearAndDisableDependentControls();

    this.countryChange.emit({ value: selectedValue, option: selectedOption });
  }

  /**
   * Handle city selection change
   * 支援多選模式的城市變更
   */
  async onCityChange(selectedValue: any): Promise<void> {
    const selectedOption = this.cityOptions.find(
      (opt) => opt.value === selectedValue
    );

    // Clear dependent fields
    this.form.patchValue(
      {
        area: null,
        expressArea: null,
        district: null,
      },
      { emitEvent: false }
    );

    // Update dependent options
    this.updateAreasForCity(selectedValue);
    this.updateExpressAreasForCity(selectedValue);

    // Update districts - 支援多選和單選
    const countryValue = this.form.get('country')?.value;

    // 多選模式：如果小區是多選且已經預載了選項，不需要重新載入
    if (!this.useMultiSelectForDistrict || (this.districtOptions.length = 0)) {
      // 單選模式或小區選項尚未載入時才載入
      await this.updateDistrictsForCity(countryValue, selectedValue);
    }

    this.cityChange.emit({ value: selectedValue, option: selectedOption });
  }

  /**
   * Handle area selection change
   */
  onAreaChange(selectedValue: any): void {
    const selectedOption = this.areaOptions.find(
      (opt) => opt.value === selectedValue
    );
    this.areaChange.emit({ value: selectedValue, option: selectedOption });
  }

  /**
   * Handle express area selection change
   */
  onExpressAreaChange(selectedValue: any): void {
    const selectedOption = this.expressAreaOptions.find(
      (opt) => opt.value === selectedValue
    );
    this.expressAreaChange.emit({
      value: selectedValue,
      option: selectedOption,
    });
  }

  /**
   * Handle district selection change
   */
  onDistrictChange(selectedValue: any): void {
    const selectedOption = this.districtOptions.find(
      (opt) => opt.value === selectedValue
    );
    this.districtChange.emit({ value: selectedValue, option: selectedOption });
  }

  /**
   * Update city options based on selected country
   * 如果是多選模式，一次載入該國家的所有城市
   */
  private updateCitiesForCountry(countryValue: any, emitEvent: boolean = true): void {
    if (!countryValue || countryValue === 'ALL') {
      if (countryValue === 'ALL') {
        this.cityOptions = [{ label: 'ALL', value: 'ALL' }];
        this.form.get('city')?.enable();
      } else {
        this.cityOptions = [];
        this.form.get('city')?.disable();
      }
      this.updateFieldValidator('city'); // 更新驗證器
      return;
    }

    const filteredCities = this.allCities.filter(city =>
      city.country === countryValue || city.countryCode === countryValue
    );

    this.cityOptions = this.prepareOptions(filteredCities);

    if (this.cityOptions.length > 0) {
      this.form.get('city')?.enable();
    } else {
      this.form.get('city')?.disable();
    }

    // 智能更新驗證器
    this.updateFieldValidator('city');

    // 多選模式：同時載入該國家的所有小區
    if (this.useMultiSelectForDistrict && this.showDistrict) {
      this.preloadDistrictsForCountry(countryValue);
    }
  }

  /**
   * Update area options based on selected city
   */
  private updateAreasForCity(cityValue: any, emitEvent: boolean = true): void {
    if (!cityValue || cityValue === 'ALL') {
      if (cityValue === 'ALL') {
        this.areaOptions = [{ label: 'ALL', value: 'ALL' }];
        this.form.get('area')?.enable();
      } else {
        this.areaOptions = [];
        this.form.get('area')?.disable();
      }
      this.updateFieldValidator('area');
      return;
    }

    const filteredAreas = this.allAreas.filter(area =>
      area.city === cityValue || area.cityCode === cityValue
    );

    this.areaOptions = this.prepareOptions(filteredAreas);

    if (this.areaOptions.length > 0) {
      this.form.get('area')?.enable();
    } else {
      this.form.get('area')?.disable();
    }

    this.updateFieldValidator('area');
  }

  /**
   * Update express area options based on selected city
   */
  private updateExpressAreasForCity(cityValue: any, emitEvent: boolean = true): void {
    if (!cityValue || cityValue === 'ALL') {
      if (cityValue === 'ALL') {
        this.expressAreaOptions = this.prepareOptions(this.allExpressAreas);
        this.form.get('expressArea')?.enable();
      } else {
        this.expressAreaOptions = [];
        this.form.get('expressArea')?.disable();
      }
      this.updateFieldValidator('expressArea');
      return;
    }

    const selectedCityOption = this.allCities.find(city =>
      city.value === cityValue || city.cityCode === cityValue
    );

    if (selectedCityOption && selectedCityOption.expressArea) {
      this.expressAreaOptions = [{
        label: selectedCityOption.expressArea,
        value: selectedCityOption.expressArea,
        expressArea: selectedCityOption.expressArea,
        city: cityValue
      }];

      if (this.allowAllOption) {
        this.expressAreaOptions.unshift({ label: 'ALL', value: 'ALL' });
      }

      this.form.get('expressArea')?.enable();
    } else {
      this.expressAreaOptions = [];
      this.form.get('expressArea')?.disable();
    }

    this.updateFieldValidator('expressArea');
  }

  /**
   * Update district options based on selected country and city
   * 支援多選模式的小區更新
   */
  private async updateDistrictsForCity(countryValue: any, cityValue: any, emitEvent: boolean = true): Promise<void> {
    if (!countryValue || !cityValue || cityValue === 'ALL' || countryValue === 'ALL') {
      this.districtOptions = [];
      this.form.get('district')?.disable();
      this.districtOptionsReady.emit([]);
      this.updateFieldValidator('district');
      return;
    }

    // 多選模式：cityValue 可能是陣列
    if (this.useMultiSelectForDistrict && Array.isArray(cityValue)) {
      await this.updateDistrictsForMultipleCities(countryValue, cityValue);
      return;
    }

    // 使用國家查詢 API
    try {

      this.commonDataService.getAssignmentDistrictBy(undefined, countryValue).subscribe({
        next: (response) => {
          const allDistricts: LocationOption[] = response.map((district: any) => ({
            label: district.districtName,
            value: district.assignmentDistrictId,
            country: countryValue,
            districtName: district.districtName,
            districtId: district.assignmentDistrictId,
            assignmentDistrictId: district.assignmentDistrictId
          }));

          this.districtOptions = this.prepareOptions(allDistricts);

          if (this.districtOptions.length > 0) {
            this.form.get('district')?.enable();
          } else {
            this.form.get('district')?.disable();
          }

          this.districtOptionsReady.emit(allDistricts);
          this.updateFieldValidator('district');
        },
        error: (error) => {
          console.error(`Failed to load districts for country ${countryValue}:`, error);
          this.districtOptions = [];
          this.form.get('district')?.disable();
          this.districtOptionsReady.emit([]);
          this.updateFieldValidator('district');
        }
      });

    } catch (error) {
      console.error('Failed to load districts:', error);
      this.districtOptions = [];
      this.form.get('district')?.disable();
      this.districtOptionsReady.emit([]);
      this.updateFieldValidator('district');
    }
  }

  /**
   * 預先載入該國家的所有小區 - 使用直接查詢國家的 API
   */
  private async preloadDistrictsForCountry(countryValue: any): Promise<void> {
    try {
      if (!countryValue || countryValue === 'ALL') {
        this.districtOptions = [];
        this.form.get('district')?.disable();
        this.districtOptionsReady.emit([]);
        return;
      }
      if(this.showDistrict){
        // 使用 commonDataService 直接通過國家查詢小區
        this.commonDataService
          .getAssignmentDistrictBy(undefined, countryValue)
          .subscribe({
            next: (response) => {

              // 轉換為 LocationOption 格式
              const allDistricts: LocationOption[] = response.map(
                (district: any) => ({
                  label: district.districtName,
                  value: district.assignmentDistrictId,
                  country: countryValue,
                  districtName: district.districtName,
                  districtId: district.assignmentDistrictId,
                  assignmentDistrictId: district.assignmentDistrictId,
                  // 保留原始資料以備不時之需
                  originalData: district,
                })
              );

              this.districtOptions = this.prepareOptions(allDistricts);

              if (this.districtOptions.length > 0) {
                this.form.get('district')?.enable();

              } else {
                this.form.get('district')?.disable();
              }

              this.districtOptionsReady.emit(allDistricts);
            },
            error: (error) => {
              this.districtOptions = [];
              this.form.get('district')?.disable();
              this.districtOptionsReady.emit([]);
            },
          });
      }
    } catch (error) {
      console.error('Failed to preload districts for country:', error);
      this.districtOptions = [];
      this.form.get('district')?.disable();
      this.districtOptionsReady.emit([]);
    }
  }

  /**
   * 為多個城市更新小區選項 - 用於多選模式
   */
  private async updateDistrictsForMultipleCities(
    countryValue: any,
    cityValues: any[]
  ): Promise<void> {
    if (!cityValues || cityValues.length === 0) {
      this.districtOptions = [];
      this.form.get('district')?.disable();
      this.districtOptionsReady.emit([]);
      return;
    }
    if(this.showDistrict){
    // 多選模式現在也直接用國家查詢，不需要逐個城市查詢
      try {
        this.commonDataService
          .getAssignmentDistrictBy(undefined, countryValue)
          .subscribe({
            next: (response) => {
              const allDistricts: LocationOption[] = response.map(
                (district: any) => ({
                  label: district.districtName,
                  value: district.assignmentDistrictId,
                  country: countryValue,
                  districtName: district.districtName,
                  districtId: district.assignmentDistrictId,
                  assignmentDistrictId: district.assignmentDistrictId,
                })
              );

              this.districtOptions = this.prepareOptions(allDistricts);

              if (this.districtOptions.length > 0) {
                this.form.get('district')?.enable();
              } else {
                this.form.get('district')?.disable();
              }

              this.districtOptionsReady.emit(allDistricts);
            },
            error: (error) => {
              console.error(
                `Failed to load districts for multiple cities in country ${countryValue}:`,
                error
              );
              this.districtOptions = [];
              this.form.get('district')?.disable();
              this.districtOptionsReady.emit([]);
            },
          });
      } catch (error) {
        console.error('Failed to load districts for multiple cities:', error);
        this.districtOptions = [];
        this.form.get('district')?.disable();
        this.districtOptionsReady.emit([]);
      }
    }
  }

  private async getAssignmentDistrict(
    country: string,
    city: string
  ): Promise<any[]> {
    var assignmentOptions: any[] = [];
    try {
      assignmentOptions =
        await this.addressServiceService.getAssignmentDistrict(country, city);
    } catch (error) {
      console.error('Get assignment district error:', error);
      this.toastService.error('get assignment district occur error');
    }
    return new Promise((resolve) => {
      resolve(
        assignmentOptions.map((x: any) => {
          return {
            label: `${x.AssignmentDistrictId}-${x.DistrictName}`,
            value: x.AssignmentDistrictId,
          };
        })
      );
    });
  }

  /**
   * Prepare options by adding "ALL" option if needed
   */
  private prepareOptions(options: LocationOption[]): LocationOption[] {
    const preparedOptions = [...options];

    if (this.allowAllOption && preparedOptions.length > 0) {
      preparedOptions.unshift({ label: 'ALL', value: 'ALL' });
    }

    return preparedOptions;
  }

  /**
   * Clear and disable all dependent controls
   */
  private clearAndDisableDependentControls(): void {
    this.areaOptions = [];
    this.expressAreaOptions = [];
    this.districtOptions = [];

    this.form.get('area')?.disable();
    this.form.get('expressArea')?.disable();
    this.form.get('district')?.disable();
  }

  /**
   * Reset all child controls
   */
  private resetAllChildControls(): void {
    this.form.get('city')?.disable();
    this.form.get('area')?.disable();
    this.form.get('expressArea')?.disable();
    this.form.get('district')?.disable();
  }

  /**
   * Check if selection is complete and emit event
   */
  private checkSelectionComplete(value: LocationData): void {
    const requiredFields = this.getRequiredFields();
    const isComplete = requiredFields.every(
      (field) =>
        value[field as keyof LocationData] !== null &&
        value[field as keyof LocationData] !== undefined &&
        value[field as keyof LocationData] !== ''
    );

    if (isComplete) {
      this.selectionComplete.emit(value);
    }
  }

  /**
   * Get list of required fields based on display configuration
   */
  private getRequiredFields(): string[] {
    const fields: string[] = [];

    if (this.showCountry) fields.push('country');
    if (this.showCity) fields.push('city');
    if (this.showArea) fields.push('area');
    if (this.showExpressArea) fields.push('expressArea');
    if (this.showDistrict) fields.push('district');

    return fields;
  }

  /**
   * Reset all selections
   */
  reset(): void {
    this.form.reset();
    this.cityOptions = [];
    this.areaOptions = [];
    this.expressAreaOptions = [];
    this.districtOptions = [];
    this.countryOptions = this.prepareOptions(this.allCountries);
    this.resetAllChildControls();
  }

  /**
   * Get current form value
   */
  getCurrentValue(): LocationData {
    return this.form.value;
  }

  // ===== 驗證相關方法 =====

  /**
   * 檢查特定欄位是否有錯誤且應該顯示
   */
  shouldShowFieldError(fieldName: string): boolean {
    if (!this.showValidation) return false;

    const control = this.form.get(fieldName);
    return !!(control && control.errors && (control.touched || this.submitted));
  }

  /**
   * 檢查特定欄位是否為必填
   */
  isFieldRequired(fieldName: string): boolean {
    return this.requiredFields.includes(fieldName);
  }

  /**
   * 根據 requiredFields 設定驗證器
   */
  /**
   * 修改：更新所有欄位驗證器
   */
  private updateFieldValidators(): void {
    this.requiredFields.forEach(fieldName => {
      this.updateFieldValidator(fieldName);
    });
  }

  /**
   * 新增：智能更新單一欄位的驗證器
   * 如果選項為空，即使在 requiredFields 中也不設為必填
   */
  private updateFieldValidator(fieldName: string): void {
    const control = this.form.get(fieldName);
    if (!control) return;

    // 檢查是否在必填清單中
    const isRequired = this.requiredFields.includes(fieldName);

    if (isRequired) {
      // 檢查選項是否為空
      const hasOptions = this.hasOptionsForField(fieldName);

      if (hasOptions) {
        // 有選項才設為必填
        control.setValidators([Validators.required]);
      } else {
        // 選項為空，移除必填驗證
        control.clearValidators();
      }
    } else {
      // 不在必填清單中，清除驗證器
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * 新增：檢查特定欄位是否有選項
   */
  private hasOptionsForField(fieldName: string): boolean {
    switch (fieldName) {
      case 'country':
        return this.countryOptions.length > 0;
      case 'city':
        return this.cityOptions.length > 0;
      case 'area':
        return this.areaOptions.length > 0;
      case 'expressArea':
        return this.expressAreaOptions.length > 0;
      case 'district':
        return this.districtOptions.length > 0;
      default:
        return false;
    }
  }

  /**
   * Check if a specific dropdown should be visible
   */
  isCountryVisible(): boolean {
    return this.showCountry;
  }
  isCityVisible(): boolean {
    return this.showCity;
  }
  isAreaVisible(): boolean {
    return this.showArea;
  }
  isExpressAreaVisible(): boolean {
    return this.showExpressArea;
  }
  isDistrictVisible(): boolean {
    return this.showDistrict;
  }

  validate(_: AbstractControl): ValidationErrors | null {
  const errors: any = {};

  if (this.requiredFields.includes('country')) {
    if (!this.form.get('country')?.value) {
      errors.countryRequired = true;
    }
  }

  if (this.requiredFields.includes('city')) {
    if (!this.form.get('city')?.value) {
      errors.cityRequired = true;
    }
  }

  // 如果有錯誤 → 回傳 error 物件
  return Object.keys(errors).length ? errors : null;
  }

}
